import { memo, useState, useCallback, useEffect, ChangeEvent } from 'react'
import { FormControl, FormLabel, FormErrorMessage, Divider, Box, SimpleGrid, Tooltip } from '@chakra-ui/react'

import { MaskedInput } from 'components'
import { useCalendarFilters } from '../../contexts/CalendarFiltersManager/Provider'
import { brazilianDateMask, hourMask } from 'utils/masks'
import { validateDate, validateHour } from 'utils/validation/validators'

function DateHourInputs() {
  const {
    dateFilterType,
    startDate,
    finalDate,
    startHour,
    finalHour,
    setStartHour,
    setFinalHour,
    setStartDate,
    setFinalDate,
  } = useCalendarFilters()

  const isExactDateType = dateFilterType === 'Data exata: '

  const [inputErrors, setInputErrors] = useState({
    startDate: false,
    finalDate: false,
    startHour: false,
    finalHour: false,
  })

  const [startDateValue, setStartDateValue] = useState(startDate)
  const [finalDateValue, setFinalDateValue] = useState(finalDate)
  const [startHourValue, setStartHourValue] = useState(startHour)
  const [finalHourValue, setFinalHourValue] = useState(finalHour)

  const updateInputError = useCallback((name: string, isError = true) => {
    setInputErrors((oldInputErrors) => {
      return { ...oldInputErrors, [name]: isError }
    })
  }, [])

  const isDateInputValueValid = useCallback(({ value, name }: { name: string; value: string }) => {
    if (!value) return
    if (value.length !== 10) return updateInputError(name)
    if (!validateDate(value)) return updateInputError(name)
    updateInputError(name, false)
    return true
  }, [])

  const isHourInputValueValid = useCallback(({ value, name }: { name: string; value: string }) => {
    if (!value) return
    if (value.length !== 5) return updateInputError(name)
    if (!validateHour(value)) return updateInputError(name)
    updateInputError(name, false)
    return true
  }, [])

  useEffect(() => {
    setStartDateValue(startDate)
  }, [startDate])

  useEffect(() => {
    setFinalDateValue(finalDate)
  }, [finalDate])

  useEffect(() => {
    setStartHourValue(startHour)
  }, [startHour])

  useEffect(() => {
    setFinalHourValue(finalHour)
  }, [finalHour])

  if (dateFilterType === '') return null

  return (
    <>
      <Box width="100%" my={2}>
        <Divider borderColor="borderColor" />
      </Box>

      <SimpleGrid width="100%" columns={{ base: 1, sm: 1, md: 1, lg: 2 }} spacing={2}>
        <FormControl isInvalid={inputErrors.startDate}>
          <FormLabel htmlFor="startDate">Data inicial</FormLabel>
          <MaskedInput
            mask={brazilianDateMask}
            id="startDate"
            name="startDate"
            value={startDateValue}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setStartDateValue(event.target.value)}
            onBlur={(event: ChangeEvent<HTMLInputElement>) => {
              const { name, value } = event.target
              const isDateValid = isDateInputValueValid({ name, value })
              if (isDateValid) setStartDate(event.target.value)
            }}
          />
          <FormErrorMessage>Data inválida</FormErrorMessage>
        </FormControl>

        <Tooltip isDisabled={!isExactDateType} label="Ao escolher data exata não é recomendado usar o valor do horário">
          <FormControl isInvalid={inputErrors.startHour}>
            <FormLabel htmlFor="startHour">Hora inicial</FormLabel>
            <MaskedInput
              isDisabled={isExactDateType}
              mask={hourMask}
              id="startHour"
              name="startHour"
              value={startHourValue}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setStartHourValue(event.target.value)}
              onBlur={(event: ChangeEvent<HTMLInputElement>) => {
                const { name, value } = event.target
                const isDateValid = isHourInputValueValid({ name, value })

                if (isDateValid) setStartHour(event.target.value)
              }}
            />
            <FormErrorMessage>Hora inválida</FormErrorMessage>
          </FormControl>
        </Tooltip>

        {dateFilterType === 'Intervalo de datas' && (
          <>
            <FormControl isInvalid={inputErrors.finalDate}>
              <FormLabel htmlFor="finalDate">Data final</FormLabel>
              <MaskedInput
                mask={brazilianDateMask}
                id="finalDate"
                name="finalDate"
                value={finalDateValue}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setFinalDateValue(event.target.value)}
                onBlur={(event: ChangeEvent<HTMLInputElement>) => {
                  const { name, value } = event.target
                  const isDateValid = isDateInputValueValid({ name, value })
                  if (isDateValid) setFinalDate(event.target.value)
                }}
              />
              <FormErrorMessage>Data inválida</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={inputErrors.finalHour}>
              <FormLabel htmlFor="finalHour">Hora final</FormLabel>
              <MaskedInput
                mask={hourMask}
                id="finalHour"
                name="finalHour"
                value={finalHourValue}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setFinalHourValue(event.target.value)}
                onBlur={(event: ChangeEvent<HTMLInputElement>) => {
                  const { name, value } = event.target
                  const isDateValid = isHourInputValueValid({ name, value })
                  if (isDateValid) setFinalHour(event.target.value)
                }}
              />
              <FormErrorMessage>Hora inválida</FormErrorMessage>
            </FormControl>
          </>
        )}
      </SimpleGrid>
    </>
  )
}

export default memo(DateHourInputs)
